import { gql, useQuery } from '@apollo/client';
import { OnlineOrderReportDeliveryOverviewMetrics } from '@client/graphql/types/graphql';
import { useMemo } from 'react';
import { DateRange, TrendsAnalyticsService } from '../../common/reservation-analytic-service';

interface OnlineOrderDeliveryOverviewData {
  onlineOrderReport_owner_getDeliveryOverview: OnlineOrderReportDeliveryOverviewMetrics;
}

interface OnlineOrderDeliveryOverviewVars {
  branchId: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

const ONLINE_ORDER_DELIVERY_OVERVIEW_QUERY = gql`
  query OnlineOrderReportDeliveryOverview($branchId: ID!, $dateRange: OnlineOrderReportDateRangeInput!) {
    onlineOrderReport_owner_getDeliveryOverview(branchId: $branchId, dateRange: $dateRange) {
      totalPickupOrders
      pickupRevenue
      totalDeliveryOrders
      deliveryRevenue
    }
  }
`;

export default function useOnlineOrderDeliveryOverview(branchId: string, dateRange: DateRange) {
  const previousDateRange = TrendsAnalyticsService.getPreviousDateRange(dateRange);

  const { data: currentData, loading: currentLoading } = useQuery<
    OnlineOrderDeliveryOverviewData,
    OnlineOrderDeliveryOverviewVars
  >(ONLINE_ORDER_DELIVERY_OVERVIEW_QUERY, {
    variables: {
      branchId,
      dateRange: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      },
    },
    skip: !branchId || !dateRange.startDate || !dateRange.endDate,
  });

  const { data: previousData, loading: previousLoading } = useQuery<
    OnlineOrderDeliveryOverviewData,
    OnlineOrderDeliveryOverviewVars
  >(ONLINE_ORDER_DELIVERY_OVERVIEW_QUERY, {
    variables: {
      branchId,
      dateRange: {
        startDate: previousDateRange.startDate,
        endDate: previousDateRange.endDate,
      },
    },
    skip: !branchId || !previousDateRange.startDate || !previousDateRange.endDate,
  });

  const analytics = useMemo(() => {
    if (
      !currentData?.onlineOrderReport_owner_getDeliveryOverview ||
      !previousData?.onlineOrderReport_owner_getDeliveryOverview
    ) {
      return null;
    }

    const current = currentData.onlineOrderReport_owner_getDeliveryOverview;
    const previous = previousData.onlineOrderReport_owner_getDeliveryOverview;

    const trends = {
      totalPickupOrders: TrendsAnalyticsService.calculateTrend(current.totalPickupOrders, previous.totalPickupOrders),
      pickupRevenue: TrendsAnalyticsService.calculateTrend(current.pickupRevenue, previous.pickupRevenue),
      totalDeliveryOrders: TrendsAnalyticsService.calculateTrend(
        current.totalDeliveryOrders,
        previous.totalDeliveryOrders,
      ),
      deliveryRevenue: TrendsAnalyticsService.calculateTrend(current.deliveryRevenue, previous.deliveryRevenue),
    };

    return {
      current,
      previous,
      trends,
    };
  }, [currentData, previousData]);

  const isLoading = currentLoading || previousLoading;

  return { analytics, isLoading };
}
